import { useState, useEffect, useRef, useCallback } from "react";
import { BASE_URL } from "../../config";
import { getTodayDateString } from "../../utils/timesheetViewHelpers";
import {
  addAppCalendarDays,
  getAppTodayIso,
  getAppWeekStartIso,
} from "../../utils/appDateTime";

const VARIANT_CONFIG = {
  admin: {
    clientsUrl: (search) =>
      `${BASE_URL}/api/clients/clients?all=1&search=${encodeURIComponent(search)}`,
    timesheetsUrl: `${BASE_URL}/api/admin/timesheets`,
    autoSelectSingleClient: false,
  },
  clientContact: {
    clientsUrl: (search) =>
      `${BASE_URL}/api/contact/assigned-clients?search=${encodeURIComponent(search)}`,
    timesheetsUrl: `${BASE_URL}/api/timesheets`,
    autoSelectSingleClient: true,
  },
};

function getAuthHeaders() {
  const token =
    localStorage.getItem("token") || localStorage.getItem("authToken");
  if (!token || token === "null" || token === "undefined" || !String(token).trim()) {
    return {};
  }
  return { Authorization: `Bearer ${token}` };
}

export default function useTimesheetsPanel({
  variant = "admin",
  active = true,
  staffRequestId = null,
} = {}) {
  const config = VARIANT_CONFIG[variant] || VARIANT_CONFIG.admin;

  const [filterClient, setFilterClient] = useState("");
  const [clientSearch, setClientSearch] = useState("");
  const [filterClientList, setFilterClientList] = useState([]);
  const [filterLocationList, setFilterLocationList] = useState([]);
  const [filterLocations, setFilterLocations] = useState([]);
  const [filterMode, setFilterMode] = useState("day");
  const [filterStartDate, setFilterStartDate] = useState(() => getTodayDateString());
  const [filterRange, setFilterRange] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [clientLoading, setClientLoading] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [timesheetPrintOrientation, setTimesheetPrintOrientation] = useState("landscape");
  const [showTimesheetPrintDropdown, setShowTimesheetPrintDropdown] = useState(false);
  const [printWithoutValues, setPrintWithoutValues] = useState(false);
  const timesheetPrintDropdownRef = useRef(null);
  const autoSelectDoneRef = useRef(false);

  const refresh = useCallback(() => {
    setReloadKey((prev) => prev + 1);
  }, []);

  const clearAllFilters = useCallback(() => {
    setFilterClient("");
    setClientSearch("");
    setFilterClientList([]);
    setFilterLocationList([]);
    setFilterLocations([]);
    setFilterMode("day");
    setFilterStartDate(getTodayDateString());
    setFilterRange("");
    setSearchTerm("");
  }, []);

  const removeFilterChip = useCallback((chip) => {
    const key = chip?.key;
    if (key === "client") {
      setFilterClient("");
      setClientSearch("");
      setFilterLocationList([]);
      setFilterLocations([]);
    }
    if (key === "locations") {
      const value = chip?.value;
      setFilterLocations((prev) =>
        value ? prev.filter((id) => String(id) !== String(value)) : [],
      );
    }
    if (key === "mode") setFilterMode("day");
    if (key === "startDate") setFilterStartDate(getTodayDateString());
    if (key === "range") setFilterRange("");
    if (key === "printWithoutValues") setPrintWithoutValues(false);
  }, []);

  // Fetch client list when filter panel would need it (debounced search)
  useEffect(() => {
    if (!active) return;

    const timer = setTimeout(async () => {
      setClientLoading(true);
      try {
        const search = (clientSearch || "").trim();
        const url = config.clientsUrl(search);
        const res = await fetch(url, {
          credentials: "include",
          headers: { ...getAuthHeaders() },
        });
        const json = await res.json();
        const rows = Array.isArray(json?.data)
          ? json.data
          : Array.isArray(json)
            ? json
            : [];
        setFilterClientList(rows);

        if (
          config.autoSelectSingleClient &&
          !autoSelectDoneRef.current &&
          rows.length === 1 &&
          !filterClient
        ) {
          autoSelectDoneRef.current = true;
          const only = rows[0];
          setFilterClient(String(only.id));
          setClientSearch(only.name || "");
        }
      } catch (err) {
        console.error("Timesheets client fetch error:", err);
        setFilterClientList([]);
      } finally {
        setClientLoading(false);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [active, clientSearch, config, filterClient]);

  // Load locations when client changes
  useEffect(() => {
    if (!active) return;
    setFilterLocations([]);
    if (!filterClient) {
      setFilterLocationList([]);
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(
          `${BASE_URL}/api/jobs/client-locations/${filterClient}`,
          { credentials: "include", headers: { ...getAuthHeaders() } },
        );
        const locs = await res.json();
        if (!cancelled) {
          setFilterLocationList(Array.isArray(locs) ? locs : []);
        }
      } catch (err) {
        console.error("Timesheets location fetch error:", err);
        if (!cancelled) setFilterLocationList([]);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [active, filterClient]);

  // Fetch timesheet data
  useEffect(() => {
    if (!active) return;

    if (!filterClient) {
      setData([]);
      setLoading(false);
      return;
    }

    const controller = new AbortController();
    let cancelled = false;

    (async () => {
      setLoading(true);
      try {
        const tsParams = new URLSearchParams();
        tsParams.set("search", searchTerm || "");
        tsParams.set("client_id", String(filterClient));
        if (staffRequestId) {
          tsParams.set("staff_request_id", String(staffRequestId));
        }
        if (filterLocations.length > 0) {
          tsParams.set("location_id", filterLocations.join(","));
        }

        const startDateForApi =
          filterStartDate ||
          (filterMode === "week"
            ? getAppWeekStartIso(getAppTodayIso())
            : getAppTodayIso());
        tsParams.set("start_date", startDateForApi);

        let rangeDays = 0;
        if (filterRange) {
          rangeDays = parseInt(filterRange, 10);
        } else {
          rangeDays = 7;
        }
        if (rangeDays > 0) {
          const endDate = addAppCalendarDays(startDateForApi, rangeDays - 1);
          tsParams.set("end_date", endDate);
        }
        if (filterRange) tsParams.set("range", filterRange);
        if (filterMode) tsParams.set("mode", filterMode);

        const fullUrl = `${config.timesheetsUrl}?${tsParams.toString()}`;
        const res = await fetch(fullUrl, {
          credentials: "include",
          headers: { ...getAuthHeaders() },
          signal: controller.signal,
        });
        const result = await res.json();
        if (!res.ok) {
          throw new Error(result?.message || `Request failed (${res.status})`);
        }
        if (!cancelled) {
          setData(result.data || []);
        }
      } catch (err) {
        if (!cancelled && err.name !== "AbortError") {
          console.error("Timesheets fetch error:", err);
          setData([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [
    active,
    config.timesheetsUrl,
    filterClient,
    filterLocations,
    filterMode,
    filterRange,
    filterStartDate,
    reloadKey,
    searchTerm,
    staffRequestId,
  ]);

  return {
    filterClient,
    setFilterClient,
    clientSearch,
    setClientSearch,
    filterClientList,
    filterLocationList,
    filterLocations,
    setFilterLocations,
    filterMode,
    setFilterMode,
    filterStartDate,
    setFilterStartDate,
    filterRange,
    setFilterRange,
    searchTerm,
    setSearchTerm,
    data,
    loading,
    clientLoading,
    timesheetPrintOrientation,
    setTimesheetPrintOrientation,
    showTimesheetPrintDropdown,
    setShowTimesheetPrintDropdown,
    printWithoutValues,
    setPrintWithoutValues,
    timesheetPrintDropdownRef,
    refresh,
    clearAllFilters,
    removeFilterChip,
  };
}
